import { html as HTML_NS } from '../xmlns';

export function isDocument(node: Node): node is Document {
  return node.nodeType === Node.DOCUMENT_NODE;
}

export function isDocumentFragment(node: Node): node is DocumentFragment {
  return node.nodeType === Node.DOCUMENT_FRAGMENT_NODE;
}

export function isElement(node: Node): node is Element {
  return node.nodeType === Node.ELEMENT_NODE;
}

export function isText(node: Node): node is Text {
  return node.nodeType === Node.TEXT_NODE || node.nodeType === Node.CDATA_SECTION_NODE;
}

export function isDocumentType(node: Node): node is DocumentType {
  return node.nodeType === Node.DOCUMENT_TYPE_NODE;
}

export function isCDataSection(node: Node): node is CDATASection {
  return node.nodeType === Node.CDATA_SECTION_NODE;
}

export function isCommentNode(node: Node): node is Comment {
  return node.nodeType === Node.COMMENT_NODE;
}

export function isParentNode(node: Node): node is Element | Document | DocumentFragment {
  return isElement(node) || isDocument(node) || isDocumentFragment(node);
}

export function isChildNode(node: Node): node is Element | Text {
  return isElement(node) || isText(node);
}

export function isRange(obj: any): obj is Range {
  const { constructor } = obj;
  return constructor && constructor.name === 'Range';
}

export function isWindow(obj: any): obj is Window {
  return obj && 'Window' in obj;
}

export function clipRange(target: Range, clip: Range) {
  if (target.compareBoundaryPoints(Range.START_TO_START, clip) < 0) {
    target.setStart(clip.startContainer, clip.startOffset);
  }
  if (target.compareBoundaryPoints(Range.END_TO_END, clip) > 0) {
    target.setEnd(clip.endContainer, clip.endOffset);
  }
}

export function nodeLength(node: Node) {
  return isText(node) ? node.length : node.childNodes.length;
}

export function createSingleNodeRange(node: Node): Range;
export function createSingleNodeRange(node: Node, star: number, end: number): Range;
export function createSingleNodeRange(node: Node, start?: number, end?: number) {
  const document = isDocument(node) ? node : node.ownerDocument;
  if (document === null) {
    return new Range();
  }

  const range = document.createRange();
  range.setStart(node, start === undefined ? 0 : start);
  range.setEnd(node, end === undefined ? nodeLength(node) : end);
  return range;
}

export function createPointRange(node: Node): Range;
export function createPointRange(node: Node, offset: number): Range;
export function createPointRange(node: Node, offset: number = 0) {
  const document = isDocument(node) ? node : node.ownerDocument;
  if (document === null) {
    return new Range();
  }

  const range = document.createRange();
  range.setStart(node, offset);
  return range;
}

export function* iterateChildNodes(node: Node, clipRange?: Range) {
  if (clipRange !== undefined) {
    for (let child: Node | null = node.firstChild; child !== null; child = child.nextSibling) {
      if (!isChildNode(child)) {
        continue;
      }
      const range = createSingleNodeRange(child);
      if ((range.compareBoundaryPoints(Range.START_TO_END, clipRange) < 0)
          || (range.compareBoundaryPoints(Range.END_TO_START, clipRange) > 0)) {
      } else {
        yield child;
      }
      range.detach();
    }
  } else {
    for (let child: Node | null = node.firstChild; child != null; child = child.nextSibling) {
      yield child;
    }
  }
}

export function getAncestorElements(node: Node) {
  const result = [];
  for (let n = isElement(node) ? node : node.parentElement; n != null; n = n.parentElement) {
    result.unshift(n);
  }
  return result;
}

export function getSelectedRangeOrAll(doc: Document) {
  const selection = doc.getSelection();
  if (selection !== null && !selection.isCollapsed) {
    return selection.getRangeAt(0).cloneRange();
  }
  const range = doc.createRange();
  range.setEnd(doc, doc.childNodes.length);
  return range;
}

export async function enterInnerContentIfNeeded(doc: Document) {
  const body = doc.body;
  if (body !== null && body.childElementCount === 1) {
    const element = body.firstElementChild!;
    if (element.namespaceURI === HTML_NS && element.localName === 'object') {
      try {
        return getContentDocument(element as HTMLObjectElement);
      } catch (e) {
        // ignore
      }
    }
  }
  return doc;
}
type HTMLContainerElement = HTMLIFrameElement | HTMLObjectElement;

export function getElementSrc(element: HTMLContainerElement) {
  if (element.localName === 'object') {
    return (element as HTMLObjectElement).data;
  }
  return (element as HTMLIFrameElement).src;
}

export async function getContentDocument(object: HTMLObjectElement,
                                         rejectIfLoading?: boolean): Promise<Document>;
export async function getContentDocument(iframe: HTMLIFrameElement,
                                         rejectIfLoading?: boolean): Promise<Document>;
export async function getContentDocument(element: HTMLContainerElement,
                                         rejectIfLoading?: boolean): Promise<Document>;
export async function getContentDocument(element: HTMLContainerElement,
                                         rejectIfLoading: boolean = false): Promise<Document> {
  const { contentDocument } = element;
  if (contentDocument && contentDocument.location) {
    if (contentDocument.location.href === getElementSrc(element)
        && contentDocument.readyState === 'complete') {
      return contentDocument;
    }
    if (rejectIfLoading) {
      throw new Error('Document is now loading.');
    }
  }
  return new Promise<Document>((resolve, reject) => {
    element.addEventListener(
      'load',
      () => {
        let doc: Document | null;
        try {
          doc = element.contentDocument;
        } catch (e) {
          reject(e);
          return;
        }
        if (doc === null) {
          reject(new Error("contentDocument can't read for some reason."));
          return;
        }
        resolve(doc);
      },
      { once: true },
    );
  });
}

export async function getContentWindow(object: HTMLObjectElement): Promise<Window | null>;
export async function getContentWindow(iframe: HTMLIFrameElement): Promise<Window | null>;
export async function getContentWindow(element: HTMLContainerElement): Promise<Window | null>;
export async function getContentWindow(element: HTMLContainerElement): Promise<Window | null> {
  const { contentWindow } = element;
  // HTMLObjectElementにはcontentWindowプロパティがあるが、一部のブラウザでしか実装されていない。
  if (contentWindow === undefined) {
    return (await getContentDocument(element)).defaultView;
  }
  if (contentWindow !== null) {
    return contentWindow;
  }
  return new Promise<Window>((resolve) => {
    element.addEventListener('load', () => resolve(element.contentWindow!));
  });
}
